import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import { AlertLog, MatchingSourcePpType, RefundLogStatus, RequestStatusValues, SourceMatchLogMetadata } from '../../types/autoRefund';
import { AlertSubscriptionStatusLabel, FulfillmentStatusLabel, RefundedExternallyLabel } from '../../types/alerts';
import LightTooltip from '../../components/common/LightTooltip';
import CopyableValue from '../../components/common/CopyableValue';
import { buildProviderAlertLink } from '../../utils/alertLogLinks';
import { pascalCase } from '../../utils/textUtils';
import { formatCurrency } from '../../utils/formatCurrency';

interface StatusColor {
  main: string;
  bg: string;
}

export const logStatusColors: Record<RefundLogStatus, StatusColor> = {
  [RefundLogStatus.SUCCESS]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.DRY_RUN]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.REFUND_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.RESOLVE_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.MATCH_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.NO_MATCH]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.ORDER_DETAILS_FAILED]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.AMBIGUOUS]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.MULTIPLE_TRANSACTIONS]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.ALREADY_REFUNDED]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.DUPLICATE_ALERT]: { main: '#ed6c02', bg: '#fff3e0' },
  [RefundLogStatus.ALREADY_REFUNDED_NO_DUPLICATE]: { main: '#ed6c02', bg: '#fff3e0' },
  [RefundLogStatus.NOT_FULFILLED]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.UNEXPECTED_FULFILLMENT_STATUS]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.CANCEL_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.MANUAL_SUCCESS]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.MANUAL_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.CM_STATUS_UPDATED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.NOTES_EDITED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.SUBSCRIPTION_CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.SUBSCRIPTION_NOT_SUBSCRIPTION]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.SUBSCRIPTION_NOT_FOUND]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.SUBSCRIPTION_STATUS_COPIED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.LOOP_CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.LOOP_ALREADY_CANCELLED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.LOOP_NOT_SUBSCRIPTION]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.MERCHANT_NO_SUBSCRIPTIONS]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.LOOP_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.KACHING_CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.KACHING_ALREADY_CANCELLED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.KACHING_NOT_SUBSCRIPTION]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.KACHING_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.COC_SUBSCRIPTION_CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.COC_SUBSCRIPTION_ALREADY_CANCELLED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.COC_NOT_SUBSCRIPTION]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.COC_SUBSCRIPTION_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.COC_VOID_INSTEAD_OF_REFUND]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.COC_REFUND_UNSETTLED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.COC_REFUND_VERIFIED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.COC_PORTAL_REFUND_SUCCESS]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.COC_PORTAL_REFUND_DRY_RUN]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.COC_REFUND_PENDING]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.CRM_REFUND_PENDING]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.CRM_REFUND_UNSETTLED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.CRM_REFUND_VERIFIED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.CRM_SUBSCRIPTION_CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.CRM_SUBSCRIPTION_ALREADY_CANCELLED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.CRM_NOT_SUBSCRIPTION]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.CRM_SUBSCRIPTION_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.CRM_ORDER_CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.CRM_ORDER_PARTIALLY_CANCELLED]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.CRM_ORDER_ALREADY_CANCELLED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.CRM_ORDER_NOT_CANCELLABLE]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.CRM_ORDER_NO_FULFILLMENT]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.CRM_ORDER_CANCEL_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.CRM_EXTERNAL_REFUND_RECORDED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.CRM_EXTERNAL_REFUND_ALREADY_REFUNDED]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.CRM_EXTERNAL_REFUND_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.SHOPIFY_REFUND_UNSETTLED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.SHOPIFY_REFUND_VERIFIED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.SHOPIFY_REFUND_PENDING]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.COC_PORTAL_REFUND_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.COC_EXTERNAL_REFUND_RECORDED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.COC_EXTERNAL_REFUND_ALREADY_REFUNDED]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.COC_EXTERNAL_REFUND_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.COC_VOIDED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.COC_VOID_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.COC_FULFILLMENT_CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.COC_FULFILLMENT_ALREADY_CANCELLED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.COC_FULFILLMENT_NOT_CANCELLABLE]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.COC_FULFILLMENT_OTHER_ITEMS]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.COC_FULFILLMENT_NOT_FOUND]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.COC_FULFILLMENT_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.COC_SHOPIFY_ORDER_CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.COC_SHOPIFY_ORDER_ALREADY_CANCELLED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.COC_SHOPIFY_ORDER_NOT_CANCELLABLE]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.COC_SHOPIFY_ORDER_CANCEL_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.COC_SHOPIFY_ORDER_NOT_FOUND]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.SHOPIFY_ORDER_CANCELLED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.SHOPIFY_ORDER_ALREADY_CANCELLED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.SHOPIFY_ORDER_NOT_CANCELLABLE]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.SHOPIFY_ORDER_CANCEL_FAILED]: { main: '#dc2626', bg: '#fee2e2' },
  [RefundLogStatus.REFUNDED_EXTERNALLY_UPDATED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.FULFILLMENT_STATUS_UPDATED]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.ETHOCA_VISA_SKIPPED]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.MAX_AMOUNT_EXCEEDED]: { main: '#ed6c02', bg: '#fff3e0' },
  [RefundLogStatus.ADYEN_MATCH_FOUND]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.ADYEN_MATCH_FOUND_DRY_RUN]: { main: '#ca8a04', bg: '#fef9c3' },
  [RefundLogStatus.ADYEN_MATCH_NOT_IN_COC]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.COC_MATCHED_ON_OTHER_ACCOUNT]: { main: '#0284c7', bg: '#e0f2fe' },
  [RefundLogStatus.SOURCE_MATCH_FOUND]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.SOURCE_MATCH_AMBIGUOUS]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.SOURCE_NO_MATCH]: { main: '#6b7280', bg: '#f3f4f6' },
  [RefundLogStatus.PREMATCHED]: { main: '#16a34a', bg: '#dcfce7' },
  [RefundLogStatus.PREMATCH_NO_MATCH]: { main: '#d97706', bg: '#fef3c7' },
  [RefundLogStatus.PREMATCH_AMBIGUOUS]: { main: '#d97706', bg: '#fef3c7' },
};

export const isErrorStatus = (status: RefundLogStatus): boolean => {
  return status === RefundLogStatus.MATCH_FAILED || status === RefundLogStatus.REFUND_FAILED || status === RefundLogStatus.RESOLVE_FAILED || status === RefundLogStatus.MANUAL_FAILED || status === RefundLogStatus.CANCEL_FAILED || status === RefundLogStatus.LOOP_FAILED || status === RefundLogStatus.KACHING_FAILED || status === RefundLogStatus.COC_SUBSCRIPTION_FAILED || status === RefundLogStatus.COC_EXTERNAL_REFUND_FAILED || status === RefundLogStatus.COC_VOID_FAILED || status === RefundLogStatus.COC_REFUND_UNSETTLED || status === RefundLogStatus.ORDER_DETAILS_FAILED || status === RefundLogStatus.COC_FULFILLMENT_FAILED || status === RefundLogStatus.COC_SHOPIFY_ORDER_CANCEL_FAILED || status === RefundLogStatus.SHOPIFY_ORDER_CANCEL_FAILED || status === RefundLogStatus.COC_PORTAL_REFUND_FAILED || status === RefundLogStatus.COC_REFUND_PENDING || status === RefundLogStatus.CRM_REFUND_PENDING || status === RefundLogStatus.CRM_REFUND_UNSETTLED || status === RefundLogStatus.CRM_SUBSCRIPTION_FAILED || status === RefundLogStatus.CRM_ORDER_CANCEL_FAILED || status === RefundLogStatus.CRM_EXTERNAL_REFUND_FAILED || status === RefundLogStatus.ALREADY_REFUNDED || status === RefundLogStatus.SHOPIFY_REFUND_UNSETTLED || status === RefundLogStatus.SHOPIFY_REFUND_PENDING;
};

const formatDateTime = (dateString: string | Date | null | undefined) => {
  if (!dateString) return '--';
  const dateStr = dateString instanceof Date ? dateString.toISOString() : dateString;
  return dayjs.utc(dateStr).format('MMM DD, YYYY HH:mm');
};

const truncateMessage = (message: string, maxLength: number = 140): { truncated: string; isTruncated: boolean } => {
  if (message.length <= maxLength) {
    return { truncated: message, isTruncated: false };
  }
  return { truncated: message.substring(0, maxLength) + '...', isTruncated: true };
};

const SuccessStatusChip = ({ message }: { message: string | null }) => {
  const colors = { main: '#16a34a', bg: '#dcfce7' };
  return (
    <Chip
      label={message}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.main,
        fontWeight: 500,
        fontSize: '0.75rem',
        height: '24px',
      }}
    />
  );
};

const ErrorStatusChip = ({ message }: { message: string | null }) => {
  const colors = { main: '#dc2626', bg: '#fee2e2' };
  return (
    <Chip
      label={message}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.main,
        fontWeight: 500,
        fontSize: '0.75rem',
        height: '24px',
      }}
    />
  );
};

export const LogStatusChip = ({ status, message, link }: { status?: RefundLogStatus; message: string | null; link?: string | null }) => {
  const defaultColors = { main: '#6b7280', bg: '#f3f4f6' };
  const colors = status ? (logStatusColors[status] || defaultColors) : defaultColors;
  const { truncated, isTruncated } = truncateMessage(message || '');

  const handleClick = (e: React.MouseEvent) => {
    if (link) {
      e.preventDefault();
      e.stopPropagation();
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  const chipContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>{truncated}</span>
      {link && <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />}
    </Box>
  );

  const chip = (
    <Chip
      label={chipContent}
      size="small"
      onClick={link ? handleClick : undefined}
      sx={{
        backgroundColor: colors.bg,
        color: colors.main,
        fontWeight: 500,
        fontSize: '0.75rem',
        height: '24px',
        cursor: link ? 'pointer' : 'default',
        '&:hover': link ? {
          textDecoration: 'underline',
          backgroundColor: colors.bg,
        } : {},
        '& .MuiChip-label': {
          padding: link ? '0 6px' : '0 8px',
        },
      }}
    />
  );

  if (isTruncated && message) {
    return (
      <LightTooltip title={message} placement="top">
        {chip}
      </LightTooltip>
    );
  }

  return chip;
};

// Void logs written before the metadata key was aligned still carry the older name.
const checkoutChampTransactionKey = (log: AlertLog): string | undefined =>
  log.metadata?.checkout_champ_transaction_key ?? log.metadata?.checkout_champ_transaction_id;

const duplicateSourceLabels: Record<string, string> = {
  ethoca: 'Ethoca',
  verifi_cdrn: 'CDRN',
  verifi_rdr: 'RDR',
};

interface OriginalAlertMetadata {
  source: string;
  key: string;
  created_at?: string;
}

const originalAlertLabel = (original: OriginalAlertMetadata): string =>
  `${duplicateSourceLabels[original.source]} ${original.key}`;

const originalAlertLink = (original: OriginalAlertMetadata, fallbackCreatedAt: string | Date) =>
  buildProviderAlertLink({
    alert_source: original.source,
    provider_alert_key: original.key,
    created_at: original.created_at ?? fallbackCreatedAt,
  });

const refundedExternallyMessage = (oldRaw?: string, newRaw?: string): string => {
  const newLabel = RefundedExternallyLabel[newRaw ?? ''] ?? '--';
  if (!oldRaw) return `Refunded Externally: ${newLabel}`;
  const oldLabel = RefundedExternallyLabel[oldRaw] ?? '--';
  return `Refunded Externally: ${oldLabel} → ${newLabel}`;
};

const adyenLookupMessage = (log: AlertLog): string => {
  const adyen = log.metadata?.adyen ?? {};
  const checkoutChamp = log.metadata?.checkout_champ ?? {};
  const notInCoc = log.status === RefundLogStatus.ADYEN_MATCH_NOT_IN_COC;
  const header = notInCoc
    ? `Adyen match (${log.match_method}), not in CoC (${checkoutChamp.lookup})`
    : `Adyen match (${log.match_method})`;
  const segments = [
    header,
    adyen.adyen_payments_id != null ? `Adyen payment #${adyen.adyen_payments_id}` : null,
    adyen.psp_reference ? `PSP ref ${adyen.psp_reference}` : null,
    adyen.order_key ? `CoC order ${adyen.order_key}` : null,
    checkoutChamp.transaction_key ? `CoC transaction ${checkoutChamp.transaction_key}` : null,
  ].filter((segment): segment is string => segment != null);
  return segments.join(' · ');
};

const sourceLabels: Record<MatchingSourcePpType, string> = {
  shopify: 'Shopify',
  authorize_net: 'Authorize.net',
};

const sourceMatchOutcome = (log: AlertLog): string => {
  if (log.status === RefundLogStatus.SOURCE_MATCH_FOUND) return `found via ${log.match_method}`;
  if (log.status === RefundLogStatus.SOURCE_MATCH_AMBIGUOUS) return 'ambiguous';
  return 'not found';
};

const sourceMatchMessage = (log: AlertLog): string => {
  const metadata = log.metadata as SourceMatchLogMetadata;
  const { source, order, note } = metadata;
  const account = source.account_id != null ? ` (acct ${source.account_id})` : '';
  const orderText = order ? `, order ${order.name}` : '';
  const noteText = note ? ` — ${note}` : '';
  return `#${source.position} ${sourceLabels[source.pp_type]}${account}: ${sourceMatchOutcome(log)}${orderText}${noteText}`;
};

interface AmbiguousMatch {
  orderKey: string;
  transactionKey: string;
  transactionDate: string;
  amount: number;
  currency: string;
  cardBin: string | null;
  cardLast4: string | null;
}

const ambiguousMatchMessage = (match: AmbiguousMatch): string => {
  const card = match.cardLast4 ? `${match.cardBin ?? ''}****${match.cardLast4}` : 'no card';
  return [match.orderKey, dayjs.utc(match.transactionDate).format('DD MMM YYYY'), formatCurrency(match.amount, match.currency), card].join(' · ');
};

interface PrematchCandidateLog {
  pp_type: string;
  account_name: string;
  payment_ref: string;
  payment_url: string;
  amount: number | null;
  currency: string | null;
  transaction_date: string;
  card_last4: string | null;
  card_brand: string | null;
}

const prematchCandidateMessage = (candidate: PrematchCandidateLog): string =>
  [
    pascalCase(candidate.pp_type),
    candidate.account_name,
    dayjs.utc(candidate.transaction_date).format('DD MMM YYYY'),
    candidate.amount == null ? 'no amount' : candidate.currency ? formatCurrency(candidate.amount, candidate.currency) : (candidate.amount / 100).toFixed(2),
    candidate.card_last4 ? `****${candidate.card_last4}` : 'no card',
  ].join(' · ');

interface RefundCheckRefund {
  transaction_id: string;
  amount: number;
  status: string;
  reason: string | null;
  date: string | null;
}

const refundCheckMessage = (refund: RefundCheckRefund, currency: string | null): string =>
  [
    `Refund ${refund.transaction_id}`,
    refund.date ? dayjs.utc(refund.date).format('DD MMM YYYY') : null,
    currency ? formatCurrency(refund.amount, currency) : (refund.amount / 100).toFixed(2),
    refund.status,
    refund.reason,
  ].filter((segment): segment is string => segment != null).join(' · ');

const RefundCheckChips = ({ log }: { log: AlertLog }) => (
  <>
    {(log.metadata?.refund_check?.refunds ?? []).map((refund: RefundCheckRefund) => (
      <LogStatusChip key={refund.transaction_id} message={refundCheckMessage(refund, log.metadata?.refund_check?.currency)} />
    ))}
  </>
);

export const LogStatusChips = ({ log }: { log: AlertLog }) => {
  if (log.status === RefundLogStatus.DRY_RUN) {
    return null;
  }
  if (log.status === RefundLogStatus.DUPLICATE_ALERT && log.metadata?.original_alert) {
    return (
      <LogStatusChip
        status={log.status}
        message={`Duplicate of ${originalAlertLabel(log.metadata.original_alert)}`}
        link={originalAlertLink(log.metadata.original_alert, log.created_at)}
      />
    );
  }
  if (log.status === RefundLogStatus.ALREADY_REFUNDED_NO_DUPLICATE) {
    const reason = log.metadata?.refund_reason ? `. Refund reason: ${log.metadata.refund_reason}` : '';
    return <LogStatusChip status={log.status} message={`Transaction already refunded — duplicate alert not found${reason}`} />;
  }
  if (log.status === RefundLogStatus.SUBSCRIPTION_STATUS_COPIED && log.metadata?.original_alert) {
    const newSubscriptionStatus = log.metadata.new_subscription_status;
    const newLabel = AlertSubscriptionStatusLabel[newSubscriptionStatus] ?? pascalCase(newSubscriptionStatus ?? '');
    return (
      <LogStatusChip
        status={log.status}
        message={`Subscription: ${newLabel} (copied from ${originalAlertLabel(log.metadata.original_alert)})`}
        link={originalAlertLink(log.metadata.original_alert, log.created_at)}
      />
    );
  }
  if (log.status === RefundLogStatus.AMBIGUOUS && log.metadata?.ambiguousMatches?.length) {
    return (
      <>
        <LogStatusChip status={log.status} message={pascalCase(log.status)} />
        {log.metadata.ambiguousMatches.map((match: AmbiguousMatch) => (
          <LogStatusChip key={`${match.orderKey}-${match.transactionKey}`} message={ambiguousMatchMessage(match)} />
        ))}
      </>
    );
  }
  if (log.status === RefundLogStatus.NOTES_EDITED) {
    return (
      <>
        <SuccessStatusChip message="Notes Updated" />
        {log.notes && <LogStatusChip message={log.notes} />}
      </>
    );
  }
  if (
    log.status === RefundLogStatus.SUBSCRIPTION_CANCELLED ||
    log.status === RefundLogStatus.SUBSCRIPTION_NOT_SUBSCRIPTION ||
    log.status === RefundLogStatus.SUBSCRIPTION_NOT_FOUND
  ) {
    const oldLabel = AlertSubscriptionStatusLabel[log.metadata?.old_subscription_status] ?? pascalCase(log.metadata?.old_subscription_status ?? '');
    const newLabel = AlertSubscriptionStatusLabel[log.metadata?.new_subscription_status] ?? pascalCase(log.metadata?.new_subscription_status ?? '');
    const newRefundedExternally = log.metadata?.new_refunded_externally;
    return (
      <>
        <LogStatusChip status={log.status} message={`Subscription: ${oldLabel} → ${newLabel}`} />
        {newRefundedExternally !== undefined && (
          <LogStatusChip status={log.status} message={refundedExternallyMessage(log.metadata?.old_refunded_externally, newRefundedExternally)} />
        )}
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.REFUNDED_EXTERNALLY_UPDATED) {
    return (
      <>
        <LogStatusChip status={log.status} message={refundedExternallyMessage(log.metadata?.old_refunded_externally, log.metadata?.new_refunded_externally)} />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.FULFILLMENT_STATUS_UPDATED) {
    const oldFulfillmentStatus = log.metadata?.old_fulfillment_status;
    const newFulfillmentStatus = log.metadata?.new_fulfillment_status;
    const newLabel = FulfillmentStatusLabel[newFulfillmentStatus] ?? pascalCase(newFulfillmentStatus ?? '');
    if (oldFulfillmentStatus) {
      const oldLabel = FulfillmentStatusLabel[oldFulfillmentStatus] ?? pascalCase(oldFulfillmentStatus);
      return <LogStatusChip status={log.status} message={`Fulfillment: ${oldLabel} → ${newLabel}`} />;
    }
    if (log.metadata?.original_alert) {
      return (
        <LogStatusChip
          status={log.status}
          message={`Fulfillment: ${newLabel} (copied from ${originalAlertLabel(log.metadata.original_alert)})`}
          link={originalAlertLink(log.metadata.original_alert, log.created_at)}
        />
      );
    }
    return <LogStatusChip status={log.status} message={`Fulfillment: ${newLabel}`} />;
  }
  if (log.status === RefundLogStatus.LOOP_CANCELLED) {
    const subId = log.metadata?.shopify_subscription_id ?? '(unknown)';
    const dryPrefix = log.dry_run ? 'Would have cancelled' : 'Cancelled';
    return <LogStatusChip status={log.status} message={`${dryPrefix} Loop subscription ${subId}`} />;
  }
  if (log.status === RefundLogStatus.LOOP_ALREADY_CANCELLED) {
    const subId = log.metadata?.shopify_subscription_id ?? '(unknown)';
    const before = log.metadata?.loop_status_before;
    const beforeSuffix = before ? ` (status: ${before})` : '';
    return <LogStatusChip status={log.status} message={`Loop subscription ${subId} already terminal${beforeSuffix}`} />;
  }
  if (log.status === RefundLogStatus.LOOP_NOT_SUBSCRIPTION) {
    const dryPrefix = log.dry_run ? 'Would have skipped Loop' : 'Skipped Loop';
    return <LogStatusChip status={log.status} message={`${dryPrefix} — order has no subscription`} />;
  }
  if (log.status === RefundLogStatus.MERCHANT_NO_SUBSCRIPTIONS) {
    return <LogStatusChip status={log.status} message="Merchant marked as no subscriptions" />;
  }
  if (log.status === RefundLogStatus.LOOP_FAILED) {
    const failedOp = log.metadata?.failed_operation === 'loop-cancel' ? 'cancel' : 'lookup';
    const subId = log.metadata?.shopify_subscription_id;
    const subSuffix = subId ? ` (subscription ${subId})` : '';
    return <LogStatusChip status={log.status} message={`Loop ${failedOp} failed${subSuffix}`} />;
  }
  if (log.status === RefundLogStatus.KACHING_CANCELLED) {
    const contractId = log.metadata?.kaching_contract_id ?? '(unknown)';
    const dryPrefix = log.dry_run ? 'Would have cancelled' : 'Cancelled';
    return <LogStatusChip status={log.status} message={`${dryPrefix} Kaching contract ${contractId}`} />;
  }
  if (log.status === RefundLogStatus.KACHING_ALREADY_CANCELLED) {
    const contractId = log.metadata?.kaching_contract_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Kaching contract ${contractId} already cancelled`} />;
  }
  if (log.status === RefundLogStatus.KACHING_NOT_SUBSCRIPTION) {
    const dryPrefix = log.dry_run ? 'Would have skipped' : 'Skipped';
    return <LogStatusChip status={log.status} message={`${dryPrefix} Kaching — order has no contract`} />;
  }
  if (log.status === RefundLogStatus.KACHING_FAILED) {
    if (log.metadata?.failed_operation === 'kaching-multiple-contracts') {
      const ids: string[] = log.metadata?.contract_ids ?? [];
      return <LogStatusChip status={log.status} message={`Kaching: ${ids.length} open contracts (${ids.join(', ')}) — not cancelled`} />;
    }
    const failedOp = log.metadata?.failed_operation === 'kaching-cancel' ? 'cancel' : 'lookup';
    const contractId = log.metadata?.kaching_contract_id;
    const contractSuffix = contractId ? ` (contract ${contractId})` : '';
    return <LogStatusChip status={log.status} message={`Kaching ${failedOp} failed${contractSuffix}`} />;
  }
  if (log.status === RefundLogStatus.COC_SUBSCRIPTION_CANCELLED) {
    const ids = log.metadata?.checkout_champ_purchase_ids?.join(', ') || '(unknown)';
    const dryPrefix = log.dry_run ? 'Would have cancelled' : 'Cancelled';
    return <LogStatusChip status={log.status} message={`${dryPrefix} CheckoutChamp subscription ${ids}`} />;
  }
  if (log.status === RefundLogStatus.COC_SUBSCRIPTION_ALREADY_CANCELLED) {
    const ids = log.metadata?.checkout_champ_purchase_ids?.join(', ') || '(unknown)';
    const before = log.metadata?.purchase_status_before;
    const beforeSuffix = before ? ` (status: ${before})` : '';
    return <LogStatusChip status={log.status} message={`CheckoutChamp subscription ${ids} already terminal${beforeSuffix}`} />;
  }
  if (log.status === RefundLogStatus.COC_NOT_SUBSCRIPTION) {
    const dryPrefix = log.dry_run ? 'Would have skipped CheckoutChamp' : 'Skipped CheckoutChamp';
    return <LogStatusChip status={log.status} message={`${dryPrefix} — order has no subscription`} />;
  }
  if (log.status === RefundLogStatus.COC_SUBSCRIPTION_FAILED) {
    const failedOp = log.metadata?.failed_operation === 'coc-cancel' ? 'cancel' : 'lookup';
    const ids = log.metadata?.checkout_champ_purchase_ids?.length ? ` (subscription ${log.metadata.checkout_champ_purchase_ids.join(', ')})` : '';
    return <LogStatusChip status={log.status} message={`CheckoutChamp ${failedOp} failed${ids}`} />;
  }
  if (log.status === RefundLogStatus.COC_REFUND_VERIFIED) {
    const reversal = log.metadata?.checkout_champ_reversal_key;
    const suffix = reversal ? ` by ${log.metadata?.checkout_champ_reversal_type ?? 'reversal'} ${reversal}` : '';
    return <LogStatusChip status={log.status} message={`Refund Verified${suffix}`} />;
  }
  if (log.status === RefundLogStatus.COC_REFUND_UNSETTLED) {
    return (
      <>
        <LogStatusChip status={log.status} message="Refund Not Settled" />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.COC_REFUND_PENDING || log.status === RefundLogStatus.CRM_REFUND_PENDING) {
    return (
      <>
        <LogStatusChip status={log.status} message="Refund Pending Settlement" />
        <RefundCheckChips log={log} />
      </>
    );
  }
  if (log.status === RefundLogStatus.ALREADY_REFUNDED && log.metadata?.refund_check) {
    return (
      <>
        <LogStatusChip status={log.status} message={pascalCase(log.status)} />
        <RefundCheckChips log={log} />
      </>
    );
  }
  if (log.status === RefundLogStatus.CRM_REFUND_VERIFIED) {
    const reversalId = log.metadata?.crm_connector_reversal_id;
    const suffix = reversalId ? ` by ${log.metadata?.crm_connector_reversal_type} ${reversalId}` : '';
    return <LogStatusChip status={log.status} message={`Refund Verified${suffix}`} />;
  }
  if (log.status === RefundLogStatus.CRM_REFUND_UNSETTLED) {
    return (
      <>
        <LogStatusChip status={log.status} message="Refund Not Settled" />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.SHOPIFY_REFUND_VERIFIED) {
    const transaction = log.metadata?.shopify_transaction_key;
    const suffix = transaction ? ` by refund transaction ${String(transaction).split('/').pop()}` : '';
    return <LogStatusChip status={log.status} message={`Refund Verified${suffix}`} />;
  }
  if (log.status === RefundLogStatus.SHOPIFY_REFUND_UNSETTLED) {
    return (
      <>
        <LogStatusChip status={log.status} message="Refund Not Settled" />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.SHOPIFY_REFUND_PENDING) {
    return <LogStatusChip status={log.status} message="Refund Pending Settlement" />;
  }
  if (log.status === RefundLogStatus.COC_VOID_INSTEAD_OF_REFUND) {
    return (
      <>
        <LogStatusChip status={log.status} message="Void Instead Of Refund" />
        {log.error_message && <LogStatusChip message={log.error_message} />}
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.COC_EXTERNAL_REFUND_RECORDED) {
    const txn = log.metadata?.checkout_champ_transaction_key ?? '(unknown)';
    const dryPrefix = log.dry_run ? 'Would have recorded' : 'Recorded';
    return <LogStatusChip status={log.status} message={`${dryPrefix} external refund for CheckoutChamp transaction ${txn}`} />;
  }
  if (log.status === RefundLogStatus.COC_EXTERNAL_REFUND_ALREADY_REFUNDED) {
    const txn = log.metadata?.checkout_champ_transaction_key ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`CheckoutChamp transaction ${txn} already refunded — review manually`} />;
  }
  if (log.status === RefundLogStatus.COC_EXTERNAL_REFUND_FAILED) {
    const failedOp = log.metadata?.failed_operation === 'coc-refund' ? 'refund' : 'lookup';
    const txn = log.metadata?.checkout_champ_transaction_key ? ` (transaction ${log.metadata.checkout_champ_transaction_key})` : '';
    return <LogStatusChip status={log.status} message={`CheckoutChamp external refund ${failedOp} failed${txn}`} />;
  }
  if (log.status === RefundLogStatus.CRM_SUBSCRIPTION_CANCELLED) {
    const ids = log.metadata?.crm_connector_subscription_ids?.join(', ') || '(unknown)';
    const dryPrefix = log.dry_run ? 'Would have cancelled' : 'Cancelled';
    return <LogStatusChip status={log.status} message={`${dryPrefix} CRM subscription ${ids}`} />;
  }
  if (log.status === RefundLogStatus.CRM_SUBSCRIPTION_ALREADY_CANCELLED) {
    const ids = log.metadata?.crm_connector_subscription_ids?.join(', ') || '(unknown)';
    const before = log.metadata?.subscription_status_before;
    const beforeSuffix = before ? ` (status: ${before})` : '';
    return <LogStatusChip status={log.status} message={`CRM subscription ${ids} already terminal${beforeSuffix}`} />;
  }
  if (log.status === RefundLogStatus.CRM_NOT_SUBSCRIPTION) {
    const dryPrefix = log.dry_run ? 'Would have skipped CRM' : 'Skipped CRM';
    return <LogStatusChip status={log.status} message={`${dryPrefix} — order has no subscription`} />;
  }
  if (log.status === RefundLogStatus.CRM_SUBSCRIPTION_FAILED) {
    const failedOp = log.metadata?.failed_operation === 'crm-cancel' ? 'cancel' : 'lookup';
    const ids = log.metadata?.crm_connector_subscription_ids?.length ? ` (subscription ${log.metadata.crm_connector_subscription_ids.join(', ')})` : '';
    return <LogStatusChip status={log.status} message={`CRM ${failedOp} failed${ids}`} />;
  }
  if (log.status === RefundLogStatus.CRM_ORDER_CANCELLED) {
    const id = log.metadata?.crm_connector_order_id ?? '(unknown)';
    const before = log.metadata?.fulfillment_status_before;
    const beforeSuffix = before ? ` (was ${before})` : '';
    return <LogStatusChip status={log.status} message={`Cancelled CRM order ${id}${beforeSuffix}`} />;
  }
  if (log.status === RefundLogStatus.CRM_ORDER_PARTIALLY_CANCELLED) {
    const id = log.metadata?.crm_connector_order_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Partially cancelled CRM order ${id} — some fulfillments could not be cancelled`} />;
  }
  if (log.status === RefundLogStatus.CRM_ORDER_ALREADY_CANCELLED) {
    const id = log.metadata?.crm_connector_order_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`CRM order ${id} already cancelled`} />;
  }
  if (log.status === RefundLogStatus.CRM_ORDER_NOT_CANCELLABLE) {
    const id = log.metadata?.crm_connector_order_id ?? '(unknown)';
    const reason = log.error_message ?? `order is already ${log.metadata?.fulfillment_status_before ?? 'unknown'}`;
    return <LogStatusChip status={log.status} message={`Fulfillment not cancelled for CRM order ${id} — ${reason}`} />;
  }
  if (log.status === RefundLogStatus.CRM_ORDER_NO_FULFILLMENT) {
    const id = log.metadata?.crm_connector_order_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`CRM order ${id} has no fulfillments`} />;
  }
  if (log.status === RefundLogStatus.CRM_ORDER_CANCEL_FAILED) {
    const id = log.metadata?.crm_connector_order_id;
    const target = id ? ` order ${id}` : '';
    return <LogStatusChip status={log.status} message={`Failed to cancel CRM${target} — review manually`} />;
  }
  if (log.status === RefundLogStatus.CRM_EXTERNAL_REFUND_RECORDED) {
    const txn = log.metadata?.crm_connector_transaction_id ?? '(unknown)';
    const dryPrefix = log.dry_run ? 'Would have recorded' : 'Recorded';
    return <LogStatusChip status={log.status} message={`${dryPrefix} external refund for CRM transaction ${txn}`} />;
  }
  if (log.status === RefundLogStatus.CRM_EXTERNAL_REFUND_ALREADY_REFUNDED) {
    const txn = log.metadata?.crm_connector_transaction_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`CRM transaction ${txn} already refunded — review manually`} />;
  }
  if (log.status === RefundLogStatus.CRM_EXTERNAL_REFUND_FAILED) {
    const failedOp = log.metadata?.failed_operation === 'crm-mark-refunded' ? 'mark-refunded' : 'lookup';
    const txn = log.metadata?.crm_connector_transaction_id ? ` (transaction ${log.metadata.crm_connector_transaction_id})` : '';
    return <LogStatusChip status={log.status} message={`CRM external refund ${failedOp} failed${txn}`} />;
  }
  if (log.status === RefundLogStatus.COC_VOIDED) {
    const txn = checkoutChampTransactionKey(log) ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Voided uncaptured CheckoutChamp authorization ${txn}`} />;
  }
  if (log.status === RefundLogStatus.COC_VOID_FAILED) {
    const txn = checkoutChampTransactionKey(log) ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Failed to void CheckoutChamp authorization ${txn} — review manually`} />;
  }
  if (log.status === RefundLogStatus.COC_FULFILLMENT_CANCELLED) {
    const id = log.metadata?.checkout_champ_fulfillment_id ?? '(unknown)';
    const before = log.metadata?.fulfillment_status_before;
    const beforeSuffix = before ? ` (was ${before})` : '';
    const dryPrefix = log.dry_run ? 'Would cancel' : 'Cancelled';
    return <LogStatusChip status={log.status} message={`${dryPrefix} fulfillment ${id}${beforeSuffix}`} />;
  }
  if (log.status === RefundLogStatus.COC_FULFILLMENT_ALREADY_CANCELLED) {
    const id = log.metadata?.checkout_champ_fulfillment_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Fulfillment ${id} already cancelled`} />;
  }
  if (log.status === RefundLogStatus.COC_FULFILLMENT_NOT_CANCELLABLE) {
    const id = log.metadata?.checkout_champ_fulfillment_id ?? '(unknown)';
    const before = log.metadata?.fulfillment_status_before ?? 'unknown';
    return <LogStatusChip status={log.status} message={`Fulfillment ${id} not cancelled — already ${before}`} />;
  }
  if (log.status === RefundLogStatus.COC_FULFILLMENT_OTHER_ITEMS) {
    const id = log.metadata?.checkout_champ_fulfillment_id ?? '(unknown)';
    const reason = log.metadata?.alerted_product_ids ? 'ships items outside the alerted transaction' : 'alerted items unknown';
    return <LogStatusChip status={log.status} message={`Fulfillment ${id} not cancelled — ${reason}`} />;
  }
  if (log.status === RefundLogStatus.COC_FULFILLMENT_NOT_FOUND) {
    const order = log.metadata?.checkout_champ_order_key ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Order ${order} has no fulfillments`} />;
  }
  if (log.status === RefundLogStatus.COC_FULFILLMENT_FAILED) {
    const id = log.metadata?.checkout_champ_fulfillment_id;
    const target = id ? `fulfillment ${id}` : 'fulfillments';
    return <LogStatusChip status={log.status} message={`Failed to cancel ${target} — review manually`} />;
  }
  if (log.status === RefundLogStatus.ADYEN_MATCH_FOUND || log.status === RefundLogStatus.ADYEN_MATCH_FOUND_DRY_RUN || log.status === RefundLogStatus.ADYEN_MATCH_NOT_IN_COC) {
    return <LogStatusChip status={log.status} message={adyenLookupMessage(log)} />;
  }
  if (log.status === RefundLogStatus.SOURCE_MATCH_FOUND || log.status === RefundLogStatus.SOURCE_MATCH_AMBIGUOUS || log.status === RefundLogStatus.SOURCE_NO_MATCH) {
    return <LogStatusChip status={log.status} message={sourceMatchMessage(log)} />;
  }
  if (log.status === RefundLogStatus.PREMATCHED) {
    const elapsed = typeof log.metadata?.elapsed_ms === 'number' ? ` (${log.metadata.elapsed_ms} ms)` : '';
    return <LogStatusChip status={log.status} message={`Pre-matched ${pascalCase(log.pp_type ?? '')} payment ${log.metadata?.payment?.id} via ${log.match_method}${elapsed}`} />;
  }
  if (log.status === RefundLogStatus.PREMATCH_NO_MATCH) {
    const { candidates_in_window, compatible, elapsed_ms } = log.metadata ?? {};
    return <LogStatusChip status={log.status} message={`Pre-match found nothing: ${candidates_in_window} in window, ${compatible} compatible (${elapsed_ms} ms)`} />;
  }
  if (log.status === RefundLogStatus.PREMATCH_AMBIGUOUS) {
    const { tier, elapsed_ms } = log.metadata ?? {};
    const candidates: PrematchCandidateLog[] = log.metadata?.candidates ?? [];
    return (
      <>
        <LogStatusChip status={log.status} message={`Pre-match ambiguous: ${candidates.length} candidates via ${tier} (${elapsed_ms} ms)`} />
        {candidates.map(candidate => (
          <LogStatusChip key={candidate.payment_url} message={prematchCandidateMessage(candidate)} link={candidate.payment_url} />
        ))}
      </>
    );
  }
  if (log.status === RefundLogStatus.COC_SHOPIFY_ORDER_CANCELLED) {
    const order = log.metadata?.shopify_order_id ?? '(unknown)';
    const store = log.metadata?.shopify_account_name;
    const storeSuffix = store ? ` on ${store}` : '';
    const dryPrefix = log.dry_run ? 'Would cancel' : 'Cancelled';
    return <LogStatusChip status={log.status} message={`${dryPrefix} Shopify order ${order}${storeSuffix} via CheckoutChamp`} />;
  }
  if (log.status === RefundLogStatus.COC_SHOPIFY_ORDER_ALREADY_CANCELLED) {
    const order = log.metadata?.shopify_order_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Shopify order ${order} already cancelled`} />;
  }
  if (log.status === RefundLogStatus.COC_SHOPIFY_ORDER_NOT_CANCELLABLE) {
    const order = log.metadata?.shopify_order_id ?? '(unknown)';
    const status = log.metadata?.shopify_fulfillment_status ?? 'unknown';
    return <LogStatusChip status={log.status} message={`Shopify order ${order} not cancelled — already ${status}`} />;
  }
  if (log.status === RefundLogStatus.COC_SHOPIFY_ORDER_NOT_FOUND) {
    const order = log.metadata?.shopify_order_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Shopify order ${order} not found in any connected store`} />;
  }
  if (log.status === RefundLogStatus.COC_SHOPIFY_ORDER_CANCEL_FAILED) {
    const order = log.metadata?.shopify_order_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Failed to cancel Shopify order ${order} via CheckoutChamp — review manually`} />;
  }
  if (log.status === RefundLogStatus.SHOPIFY_ORDER_CANCELLED) {
    const order = log.metadata?.shopify_order_id ?? '(unknown)';
    const dryPrefix = log.dry_run ? 'Would cancel' : 'Cancelled';
    return <LogStatusChip status={log.status} message={`${dryPrefix} Shopify order ${order}`} />;
  }
  if (log.status === RefundLogStatus.SHOPIFY_ORDER_ALREADY_CANCELLED) {
    const order = log.metadata?.shopify_order_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Shopify order ${order} already cancelled`} />;
  }
  if (log.status === RefundLogStatus.SHOPIFY_ORDER_NOT_CANCELLABLE) {
    const order = log.metadata?.shopify_order_id ?? '(unknown)';
    const reason = log.error_message ?? log.metadata?.shopify_fulfillment_status ?? 'unknown';
    return <LogStatusChip status={log.status} message={`Shopify order ${order} not cancelled — ${reason}`} />;
  }
  if (log.status === RefundLogStatus.SHOPIFY_ORDER_CANCEL_FAILED) {
    const order = log.metadata?.shopify_order_id ?? '(unknown)';
    return <LogStatusChip status={log.status} message={`Failed to cancel Shopify order ${order} — review manually`} />;
  }
  if (log.status === RefundLogStatus.ETHOCA_VISA_SKIPPED) {
    return (
      <>
        <LogStatusChip status={log.status} message="Visa Refund Disabled" />
        {log.alert_request === RequestStatusValues.SUCCESS && <SuccessStatusChip message="Alert Resolved as Other / Not Refunded" />}
        {log.alert_request === RequestStatusValues.FAILED && <ErrorStatusChip message="Alert Resolve Failed" />}
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.MAX_AMOUNT_EXCEEDED) {
    const limit = log.metadata?.refund_max_amount;
    return (
      <>
        <LogStatusChip status={log.status} message={limit != null ? `Max Refund Amount Exceeded (${formatCurrency(limit, 'USD', true, 0)})` : 'Max Refund Amount Exceeded'} />
        {log.alert_request === RequestStatusValues.SUCCESS && <SuccessStatusChip message="Alert Resolved as Declined / Not Refunded" />}
        {log.alert_request === RequestStatusValues.FAILED && <ErrorStatusChip message="Alert Resolve Failed" />}
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.NOT_FULFILLED) {
    const reason = log.error_message ?? 'left for manual handling';
    return <LogStatusChip status={log.status} message={`Unfulfilled order not cancelled — ${reason}`} />;
  }
  if (log.status === RefundLogStatus.UNEXPECTED_FULFILLMENT_STATUS) {
    return <LogStatusChip status={log.status} message={`Order left untouched — ${log.error_message ?? 'unexpected fulfillment status'}`} />;
  }
  if (log.status === RefundLogStatus.CANCEL_FAILED) {
    return (
      <>
        <LogStatusChip status={log.status} message="Failed to cancel unfulfilled order — review manually" />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.CANCELLED && log.refund_request === RequestStatusValues.SUCCESS) {
    return (
      <>
        <SuccessStatusChip message="Order Cancelled & Refunded" />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.refund_request === RequestStatusValues.SUCCESS) {
    return (
      <>
        <SuccessStatusChip message="Refund Done" />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.refund_request === RequestStatusValues.FAILED) {
    return (
      <>
        <ErrorStatusChip message="Refund Failed" />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.alert_request === RequestStatusValues.SUCCESS) {
    return (
      <>
        <SuccessStatusChip message="Alert Resolved" />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.alert_request === RequestStatusValues.FAILED) {
    return (
      <>
        <ErrorStatusChip message="Alert Resolve Failed" />
        {log.notes && <LogStatusChip message={`Notes: ${log.notes}`} />}
      </>
    );
  }
  if (log.status === RefundLogStatus.CM_STATUS_UPDATED) {
    return <LogStatusChip status={log.status} message={getCmStatusMessage(log)} />;
  }
  return <LogStatusChip status={log.status} message={pascalCase(log.status)} />;
};


// metadata: {"cm_notes":"Already refunded","new_cm_status":"closed","old_cm_status":"action_needed","cm_status_reason":"duplicate"}
// expectted output: Status updated. Closed -> Action Needed (Duplicate). Note: Already Refunded.
const getCmStatusMessage = (log: AlertLog): string => {
    return "Status updated. " + log.metadata?.old_cm_status + " -> " + log.metadata?.new_cm_status + " (" + log.metadata?.cm_status_reason + "). Note: " + log.metadata?.cm_notes;
};

interface ActivityLogProps {
  logs: AlertLog[] | [];
}

export default function ActivityLog({ logs }: ActivityLogProps) {
  if (!logs || logs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
        Activity Log
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell sx={{ fontWeight: 600, width: '180px' }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '100px' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Info</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log: AlertLog) => (
              <TableRow key={log.log_id} hover>
                <TableCell>
                  {formatDateTime(log.created_at as string)}
                </TableCell>
                <TableCell>
                  {log.trigger_source ? pascalCase(log.trigger_source) : '--'}
                </TableCell>
                <TableCell>
                  <Box>
                    {log.dry_run && <LogStatusChip status={log.status} message="Dry Run" />}
                    <LogStatusChips log={log} />
                    {isErrorStatus(log.status) && log.error_message && <LogStatusChip status={log.status} message={log.error_message} />}
                    {log.matched_order_name && (
                      <Chip
                        size="small"
                        label={
                          <CopyableValue
                            text={`${pascalCase(log.pp_type ?? '')} ${log.matched_order_name}`.trim()}
                            copyValue={log.matched_order_name}
                            href={log.matched_order_url}
                            copyTitle="Copy order name"
                            iconFontSize="0.8rem"
                            textSx={{ fontSize: '0.75rem', fontWeight: 500 }}
                          />
                        }
                        sx={{
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280',
                          height: '24px',
                          '& .MuiChip-label': { display: 'flex', px: 0.75 },
                        }}
                      />
                    )}
                    {log.user_email && <LogStatusChip message={`${log.user_email}`} />}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
